import { PATHS } from 'constants/paths';
import { WIRCreatedSections, WIRStageGate1Sections } from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  updateActiveWIRStep,
  updateOriginalSelectedWIR,
  updateSelectedSection,
  updateSelectedWIR,
} from '../../../../redux/WellGuardian';
import { useCountryState, useSelectedSite, useSelectedWIR } from '../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { CountryData, getAssetFromSite } from 'types/wellGuardian';

export const useFetchWgWIRCreationData = () => {
  const intl = useIntl();
  const [openSaveModal, setOpenSaveModal] = useState<boolean>(false);
  const [wirCreateLoading, setWirCreateLoading] = useState<boolean>(false);

  const wir = useSelectedWIR();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countryState = useCountryState();
  const fetchUserSite = useSelectedSite();

  const saveAndCreateWir = useCallback(async () => {
    const wirCreatePayload = {
      site: fetchUserSite,
      asset: getAssetFromSite(fetchUserSite, countryState),
      originatorId: wir?.phases?.wirCreated?.sections?.initialRequestInformation?.originatorId,
      originatorName: wir?.phases?.wirCreated?.sections?.initialRequestInformation?.originatorName,
      interventionTitle:
        wir?.phases?.wirCreated?.sections?.initialRequestInformation?.interventionTitle,
      wellId: wir?.wellId,
      well: wir?.well,
      expectedEngineeringTime: Number(wir?.expectedEngineeringTime),
      isUnplannedReactiveScope: wir?.isUnplannedReactiveScope,
      interventionType:
        wir?.phases?.wirCreated?.sections?.initialRequestInformation?.interventionType,
      interventionComplexity:
        countryState === CountryData.congo
          ? 'ROUTINE_3'
          : wir?.phases?.wirCreated?.sections?.initialRequestInformation?.interventionComplexity,
      objectives: wir?.phases?.wirCreated?.sections?.initialRequestInformation?.objectives,
      interventionDate:
        wir?.phases?.wirCreated?.sections?.initialRequestInformation?.interventionDate,
      priority: wir?.phases?.wirCreated?.sections?.initialRequestInformation?.priority,
      scope: wir?.phases?.wirCreated?.sections?.initialRequestInformation?.scope,
    };
    setWirCreateLoading(true);
    const wirResponse = await wellGuardianApiClient.createWIR(wirCreatePayload, countryState);
    if (wirResponse?.wellId && wirResponse?.wirNumber) {
      toast.success(
        intl.formatMessage({
          id: 'wellGuardian.wir.wirCreation.wirAdded',
        }),
        {
          hideProgressBar: false,
          theme: 'colored',
        },
      );
      dispatch(updateSelectedWIR(wirResponse));
      dispatch(updateOriginalSelectedWIR(wirResponse));
      dispatch(updateActiveWIRStep(0));
      dispatch(
        countryState === CountryData.denmark
          ? updateSelectedSection(WIRStageGate1Sections.interventionJustification)
          : updateSelectedSection(WIRCreatedSections.initialRequestInformation),
      );
      navigate(
        generatePath(PATHS.WELL_GUARDIAN_WIR_CREATION, {
          tuwi: wirResponse.wellId,
          wirId: wirResponse.wirNumber,
          country: wirResponse.country,
        }),
      );
    } else {
      toast.error(
        intl.formatMessage({
          id: 'wellGuardian.wir.wirCreation.wirAdditionError',
        }),
        {
          hideProgressBar: false,
          theme: 'colored',
        },
      );
    }
    setWirCreateLoading(false);
    setOpenSaveModal(false);
  }, [fetchUserSite, countryState, wir, intl, dispatch, navigate]);

  return {
    wirCreateLoading,
    openSaveModal,
    setOpenSaveModal,
    saveAndCreateWir,
  };
};
